// The triage list: things pulled from connected inboxes (email, Instagram)
// that need a human decision. Renders nothing when empty — calm by default.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Mail, Instagram, Check, X, ExternalLink, Sparkles, Receipt } from 'lucide-react';
import { wholesaleApi } from '../services/api';

const ICONS: Record<string, any> = { email: Mail, instagram: Instagram, catalog: Sparkles };

export default function ActionList() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({
    queryKey: ['action-items'],
    queryFn: async () => (await wholesaleApi.getActions()).data?.data || [],
    refetchOnWindowFocus: true, staleTime: 30_000,
  });
  const resolve = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'done' | 'dismissed' }) => wholesaleApi.resolveAction(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['action-items'] }),
  });
  const [invoiceFor, setInvoiceFor] = useState<any>(null);
  const [invPrice, setInvPrice] = useState('');
  const [invEmail, setInvEmail] = useState('');
  const [invMsg, setInvMsg] = useState<string | null>(null);
  const approveInvoice = useMutation({
    mutationFn: async () => (await wholesaleApi.approveInvoiceAction(invoiceFor.id, Math.round(parseFloat(invPrice) * 100), invEmail)).data?.data,
    onSuccess: (d: any) => {
      setInvMsg(`Invoice sent — Shopify draft ${d?.draftName || ''} is on its way to ${invEmail}.`);
      setInvoiceFor(null);
      qc.invalidateQueries({ queryKey: ['action-items'] });
    },
    onError: (e: any) => setInvMsg(e?.response?.data?.error?.message || "Couldn't create the invoice."),
  });
  const igHandle = (it: any) => {
    const c = String(it?.payload?.contact || '');
    return c.startsWith('@') ? c.slice(1) : null;
  };
  if (!items.length) return null;
  return (
    <div className="card p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-semibold text-slate-900">Needs a look</h2>
        <span className="text-xs text-slate-400">{items.length} from your inbox</span>
      </div>
      <div className="space-y-2">
        {items.slice(0, 8).map((it: any) => {
          const Icon = ICONS[it.source] || Mail;
          return (
            <div key={it.id} className="flex items-start gap-3 rounded-xl border border-slate-100 px-3 py-2.5">
              <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 truncate">{it.title}</p>
                {it.summary && <p className="text-xs text-slate-500 truncate">{it.summary}</p>}
              </div>
              {it.category === 'quote' && igHandle(it) && (
                <a href={`https://instagram.com/${igHandle(it)}`} target="_blank" rel="noopener noreferrer" className="p-1.5 text-slate-400 hover:text-fuchsia-600" title={`Reply to @${igHandle(it)} on Instagram`}>
                  <Instagram className="w-4 h-4" />
                </a>
              )}
              {it.category === 'quote' && (
                <button onClick={() => {
                    setInvoiceFor(it);
                    setInvPrice(String(((it.payload?.estimateCents || 0) / 100) || ''));
                    setInvEmail(String(it.payload?.contact || '').includes('@') && !String(it.payload?.contact || '').startsWith('@') ? String(it.payload.contact) : '');
                    setInvMsg(null);
                  }} className="p-1.5 text-slate-400 hover:text-emerald-600" title="Approve & send invoice via your Shopify">
                  <Receipt className="w-4 h-4" />
                </button>
              )}
              {it.link && (
                <a href={it.link} target="_blank" rel="noopener noreferrer" className="p-1.5 text-slate-400 hover:text-primary-600" title="Open the source">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              <button onClick={() => resolve.mutate({ id: it.id, status: 'done' })} className="p-1.5 text-slate-400 hover:text-emerald-600" title="Done">
                <Check className="w-4 h-4" />
              </button>
              <button onClick={() => resolve.mutate({ id: it.id, status: 'dismissed' })} className="p-1.5 text-slate-300 hover:text-red-400" title="Not relevant">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      {invMsg && <p className="mt-3 text-xs text-slate-500">{invMsg}</p>}

      {invoiceFor && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-end sm:items-center justify-center p-4" onClick={() => setInvoiceFor(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 pb-safe" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-900 mb-1">Approve & invoice</h3>
            <p className="text-xs text-slate-500 mb-4">{invoiceFor.summary}</p>
            <label className="block text-xs font-medium text-slate-600 mb-1">Final price (your call — the estimate was a starting point)</label>
            <div className="flex items-center gap-1 mb-3">
              <span className="text-slate-400">$</span>
              <input type="number" min="1" value={invPrice} onChange={(e) => setInvPrice(e.target.value)} className="input flex-1" />
            </div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Customer email (the Shopify invoice goes here)</label>
            <input type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} placeholder="them@email.com" className="input w-full mb-4" />
            <div className="flex gap-2">
              <button onClick={() => setInvoiceFor(null)} className="btn btn-secondary flex-1">Cancel</button>
              <button onClick={() => approveInvoice.mutate()} disabled={!invEmail.includes('@') || !(parseFloat(invPrice) > 0) || approveInvoice.isPending} className="btn btn-primary flex-1">
                {approveInvoice.isPending ? 'Sending…' : 'Send invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
